import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { Site } from '../../../models/site';
import { User } from '../../../models/user';
import { InspectionModule } from '../../../enums/inspection-module';
import { ViewRepresentation } from '../../../enums/representation';
import {
  selectDetectionTotal,
  selectFilteredDetectionsTotal
} from '../../../features/detection/detection.selectors';
import { selectVolumeTotal } from '../../../features/volume/volume.selectors';
import {
  selectMeasureTotal
} from '../../../features/measure/measure.selectors';
import { selectCutTotal } from '../../../features/cut/cut.selectors';
import {
  selectTrackerElevationTotal,
  selectTrackerImageTotal
} from '../../../features/tracker/tracker.selectors';
import {
  selectConfigDisplayCuts,
  selectConfigDisplayDetections,
  selectConfigDisplayElevations,
  selectConfigDisplayMarkers,
  selectConfigDisplayMeasures,
  selectConfigDisplayVolumes,
  selectConfigViewRepresentation
} from '../../../features/config/config.selectors';
import {
  selectCurrentTilesetConfig,
} from '../../../features/tileset-config/tileset-config.selectors';
import {
  setConfigDisplayCuts,
  setConfigDisplayDetections,
  setConfigDisplayElevations,
  setConfigDisplayMarkers,
  setConfigDisplayMeasures, setConfigDisplayVolumes
} from '../../../features/config/config.actions';
import { selectCurrentSite } from '../../../features/site/site.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';


@Component({
    selector: 'viewer-display-panel',
    templateUrl: './viewer-display-panel.component.html',
    styleUrls: ['./viewer-display-panel.component.scss'],
    standalone: false
})
export class ViewerDisplayPanelComponent {

  site: Site | null | undefined;
  user: User | null | undefined;
  inspectionModule = InspectionModule;
  perreadPipelineAvailable: boolean = false;
  isPointCloudDisplayed: boolean = false;
  selectedViewRepresentation: number | undefined;
  viewRepresentation = ViewRepresentation;
  collapsed = true;

  readonly site$ = this.store.select(selectCurrentSite);

  readonly filteredDetectionCount$ = this.store.select(selectFilteredDetectionsTotal);
  readonly detectionCount$ = this.store.select(selectDetectionTotal);
  readonly volumeCount$ = this.store.select(selectVolumeTotal);
  readonly measureCount$ = this.store.select(selectMeasureTotal);
  readonly cutCount$ = this.store.select(selectCutTotal);
  readonly elevationCount$ = this.store.select(selectTrackerElevationTotal);
  readonly markerCount$ = this.store.select(selectTrackerImageTotal);

  readonly hasAnyAnnotation$ = combineLatest([
    this.detectionCount$,
    this.volumeCount$,
    this.measureCount$,
    this.cutCount$,
    this.elevationCount$,
    this.markerCount$
  ]).pipe(
    map(counts => counts.some(count => count > 0))
  );

  readonly isDisplayMeasuresChecked$ = this.store.select(selectConfigDisplayMeasures);
  readonly isDisplayCutsChecked$ = this.store.select(selectConfigDisplayCuts);
  readonly isDisplayElevationsChecked$ = this.store.select(selectConfigDisplayElevations);
  readonly isDisplayMarkersChecked$ = this.store.select(selectConfigDisplayMarkers);
  readonly isDisplayVolumesChecked$ = this.store.select(selectConfigDisplayVolumes);
  readonly isDisplayDetectionsChecked$ = this.store.select(selectConfigDisplayDetections);
  readonly selectedTilesetConfig$ = this.store.select(selectCurrentTilesetConfig);

  constructor(private store: Store) {
    this.handleViewConfiguration();


  }

  private handleViewConfiguration(): void {
    this.store.select(selectConfigViewRepresentation)
      .pipe(takeUntilDestroyed())
      .subscribe((viewRepresentation: ViewRepresentation) => {
        this.selectedViewRepresentation = viewRepresentation;
        this.isPointCloudDisplayed = viewRepresentation == ViewRepresentation.POINT_CLOUD
          || viewRepresentation == ViewRepresentation.SPLITTED_POINT_CLOUD
          || viewRepresentation == ViewRepresentation.DIFFERENTIAL;
      });
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  onToggleChangeDisplayMeasures(event: any) {
    this.store.dispatch(setConfigDisplayMeasures({display: event.checked}));
  }

  onToggleChangeDisplayCuts(event: any) {
    this.store.dispatch(setConfigDisplayCuts({display: event.checked}));
  }

  onToggleChangeDisplayElevations(event: any) {
    this.store.dispatch(setConfigDisplayElevations({display: event.checked}));
  }

  onToggleChangeDisplayMarkers(event: any) {
    this.store.dispatch(setConfigDisplayMarkers({display: event.checked}));
  }

  onToggleChangeDisplayDetections(event: any) {
    this.store.dispatch(setConfigDisplayDetections({display: event.checked}));
  }

  onToggleChangeDisplayVolumes(event: any) {
    this.store.dispatch(setConfigDisplayVolumes({display: event.checked}));
  }

}
